import React from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { Chip, Stack, useMediaQuery } from '@mui/material';
import { useLiveQuery } from 'dexie-react-hooks';
import { t } from 'i18next';
import { useLoadPricingPage } from '../../app/useLoadPricingPage';
import { getCurrentMailboxQuery } from '../../model/mailbox';
import theme, { WHITE_HEX, PRIMARY_COLOR_HEX, PURPLE_HEX, LIGHT_GRAY_HEX } from '../../theme';
import PaywallBullet from './PaywallBullet';
import TopBar from './TopBar';
import DiscountIcon from '@mui/icons-material/Discount';
import PlansContainer from '../../components/PlansPopup/PlansContainer/PlansContainer';
import HorizontalPlansContainer from './HorizontalPlansContainer';
import { WebViewDetector } from '../../services/webViewDetector/webViewDetector';
import { usePricingPageActions } from './usePricingPageActions';

export type PricingPageProps = {
  userEmailFromURL?: string;
};

export default function PricingPage(props: PricingPageProps) {
  const { userEmailFromURL } = props;

  const mailbox = useLiveQuery(getCurrentMailboxQuery);

  const userEmail = mailbox?.email_address || userEmailFromURL;

  const aboveMedium = useMediaQuery(theme.breakpoints.up('md'));
  const aboveSmall = useMediaQuery(theme.breakpoints.up('sm'));

  const isIOS = WebViewDetector.getDeviceOS(navigator.userAgent) === 'ios';
  const supportsAppStore = isIOS;

  const BULLETS: string[] = [
    'unlimitedUnsubscribes',
    'unlimitedBulkDeletes',
    'forAllInboxes',
    ...(supportsAppStore ? ['cleanPhotos'] : []),
    'neverSellData',
  ];

  const {
    discountPercentage,
    includeLifetime,
    promoCode,
    returnUrl: returnUrlFromParams,
    source,
  } = useLoadPricingPage(mailbox);

  const returnUrl = returnUrlFromParams;

  const { onPlanCheckout } = usePricingPageActions({
    userEmail,
    discountPercentage,
    includeLifetime,
    promoCode,
    returnUrl,
    source,
    aboveMedium,
  });

  const renderHorizontalPlans = () => {
    return (
      <Container maxWidth='lg' component='main'>
        <HorizontalPlansContainer onCheckout={onPlanCheckout} />
      </Container>
    );
  };

  const renderVerticalPlans = () => {
    return (
      <Container maxWidth='sm' component='main' sx={{ marginTop: aboveMedium ? 0 : 4 }}>
        <PlansContainer
          mailbox={mailbox}
          buttonText={t('unlockAndStartCleaning') as string}
          onCheckout={onPlanCheckout}
        />
      </Container>
    );
  };

  return (
    <Stack
      sx={{
        minHeight: '100vh',
        background: WHITE_HEX,
        gap: aboveMedium ? 4 : 2,
      }}
    >
      <TopBar userEmail={userEmail} />
      <Container
        disableGutters
        maxWidth='lg'
        component='main'
        sx={{
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {!!discountPercentage && (
          <Chip
            icon={<DiscountIcon color='white' fontSize='small' />}
            sx={{
              background: `linear-gradient(120deg,${PRIMARY_COLOR_HEX} 13.4%,${PURPLE_HEX} 86.6%)`,
              color: WHITE_HEX,
              fontWeight: 600,
              marginBottom: aboveSmall ? 5 : 4,
              marginTop: aboveSmall ? '-4rem' : 0,
              padding: 2,
              fontSize: '1rem',
            }}
            label={t('xOffAllPlans', { percentage: discountPercentage * 100 })}
          />
        )}
        <Typography
          component='h1'
          variant='h2'
          fontWeight={800}
          align='center'
          color='text.primary'
          maxWidth={aboveMedium ? 'unset' : '80%'}
          fontSize={aboveMedium ? '4rem' : aboveSmall ? '2.5rem' : '2rem'}
        >
          {t('pricingPageTitle')}
        </Typography>
        {/* {!nativeAppFunnelMode && (
          <Typography
            variant='h5'
            align='center'
            color='text.primary'
            component='p'
            mt={'1rem'}
            fontSize={aboveMedium ? '1.6rem' : '1.2rem'}
          >
            {t('pricingPageSubtitle')}
          </Typography>
        )} */}
      </Container>
      <Box
        sx={{
          display: 'flex',
          rowGap: aboveMedium ? 3 : 2,
          columnGap: aboveMedium ? 3 : 2,
          flexWrap: 'wrap',
          justifyContent: 'center',
          px: 2,
          borderBottom: aboveMedium ? `1px ${LIGHT_GRAY_HEX} solid` : 'unset',
          pb: aboveMedium ? 6 : 0,
        }}
      >
        {BULLETS.map((bullet) => (
          <PaywallBullet key={bullet} text={t(bullet)} />
        ))}
      </Box>
      {aboveMedium ? renderHorizontalPlans() : renderVerticalPlans()}
    </Stack>
  );
}
